import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { syncNow } from '@/lib/sync';
import { toast } from '@/stores/toastStore';
import { useAuthStore } from '@/stores/authStore';
import { date as fmtDate, dateTime, num } from '@/lib/format';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatusChip } from '@/components/ui/StatusChip';
import { CheckboxInput, SelectInput, TextInput } from '@/components/ui/Field';

/**
 * Fuel sync (0058): the old platform's (Analytix) fuel stations and
 * receiving log, pulled every 15 minutes. The API key goes into Supabase
 * Vault and is never shown again; this screen sees only its last four
 * characters. Everything here goes through admin-only functions.
 */
interface Config {
  enabled: boolean;
  base_url: string;
  fills_form: string;
  receipts_form: string;
  sync_from: string;
  lookback_days: number;
  has_key: boolean;
  key_hint: string | null;
  key_set_at: string | null;
  key_set_by: string | null;
}
interface Run {
  id: number;
  trigger: string;
  started_at: string;
  finished_at: string | null;
  status: 'running' | 'ok' | 'failed';
  window_from: string | null;
  fetched: number;
  inserted: number;
  updated: number;
  linked: number;
  unchanged: number;
  skipped: number;
  rejected: number;
  message: string | null;
}
interface Reject {
  form: string;
  record: string;
  reason: string;
  submitted_at: string | null;
  branch: string | null;
  what: string | null;
  litres: string | null;
}
interface State {
  config: Config;
  points: { branch: string; tank_id: string | null; tank: string | null }[];
  tanks: { id: string; name: string; kind: string }[];
  runs: Run[];
  rejects: Reject[];
  totals: { fills: number; receipts: number; rejected: number };
}

const RUN_TONE = { running: 'amber', ok: 'green', failed: 'red' } as const;

export function FuelSyncPage() {
  const isAdmin = useAuthStore((s) => s.profile?.role === 'admin');
  const [state, setState] = useState<State | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<Config | null>(null);
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const load = useCallback(async () => {
    const { data, error: e } = await supabase.rpc('fuel_sync_state');
    if (e) { setError(e.message); return; }
    const s = data as State;
    setState(s);
    setForm((f) => f ?? s.config);
    setError(null);
  }, []);

  useEffect(() => { if (isAdmin) void load(); }, [isAdmin, load]);
  // While a run is going, look again every 10 seconds.
  const running = state?.runs.some((r) => r.status === 'running');
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => void load(), 10_000);
    return () => clearInterval(t);
  }, [running, load]);

  async function call(what: string, fn: string, args: Record<string, unknown> | undefined, ok: string) {
    setBusy(what);
    try {
      const { data, error: e } = await supabase.rpc(fn, args);
      if (e) throw e;
      const s = data as State;
      setState(s);
      setForm(s.config);
      toast.success(ok);
      return true;
    } catch (e) {
      toast.error((e as Error)?.message ?? 'Not saved');
      return false;
    } finally {
      setBusy(null);
    }
  }

  if (!isAdmin) {
    return <Panel title="Fuel sync"><p className="muted">Only an administrator can manage the fuel sync.</p></Panel>;
  }
  if (error) return <Panel title="Fuel sync"><div className="issue issue--block" style={{ display: 'block' }}>{error}</div></Panel>;
  if (!state || !form) return <div className="centered"><span className="spinner" /></div>;

  const c = state.config;
  const last = state.runs[0];
  const lastOk = state.runs.find((r) => r.status === 'ok');
  const unlinked = state.points.filter((p) => !p.tank_id);

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <Panel title="Fuel sync · old platform (Analytix)"
        actions={(
          <Button onClick={() => void call('run', 'fuel_sync_run', undefined, 'Sync started — the results appear within a minute')}
            loading={busy === 'run'} disabled={!c.has_key || !!running}>Sync now</Button>
        )}>
        <p className="small muted" style={{ marginTop: 0 }}>
          The old fuel stations keep capturing on the old platform. Their fills and deliveries are pulled every
          15 minutes, re-reading the last {c.lookback_days} days so corrections there follow here. Each record is
          taken once; the fills already imported from the history (0054) are linked, not added again.
        </p>
        <div className="grid grid--stats">
          <div>
            <div className="small muted">Status</div>
            {!c.has_key ? <StatusChip tone="grey">No API key</StatusChip>
              : !c.enabled ? <StatusChip tone="amber">Switched off</StatusChip>
                : last?.status === 'failed' ? <StatusChip tone="red">Last run failed</StatusChip>
                  : <StatusChip tone="green">Syncing every 15 min</StatusChip>}
          </div>
          <div><div className="small muted">Last good run</div><strong>{lastOk ? dateTime(lastOk.finished_at) : '—'}</strong></div>
          <div><div className="small muted">Fills taken</div><strong>{num(state.totals.fills)}</strong></div>
          <div><div className="small muted">Deliveries taken</div><strong>{num(state.totals.receipts)}</strong></div>
          <div><div className="small muted">Not taken</div><strong>{num(state.totals.rejected)}</strong></div>
        </div>
        {last?.status === 'failed' && last.message && (
          <div className="issue issue--block" style={{ display: 'block', marginTop: 'var(--sp-2)' }}>{last.message}</div>
        )}
      </Panel>

      <Panel title="API key">
        {c.has_key ? (
          <p style={{ marginTop: 0 }}>
            A key ending <strong>{c.key_hint}</strong> is stored{c.key_set_by ? ` (set by ${c.key_set_by}` : ' ('}
            {c.key_set_at ? ` on ${fmtDate(c.key_set_at)})` : ')'}. It is kept encrypted and is never shown again.
          </p>
        ) : (
          <p style={{ marginTop: 0 }} className="muted">
            In the old platform: Organisation settings → API Tokens → New token, role <strong>Viewer</strong>.
            Paste the <code>axt_…</code> key here.
          </p>
        )}
        <div className="row" style={{ alignItems: 'flex-end', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 320px' }}>
            <TextInput id="fs-key" type="password" autoComplete="off" label={c.has_key ? 'Replace the key' : 'API key'}
              value={key} onChange={setKey} placeholder="axt_…" />
          </div>
          <Button onClick={async () => {
            if (await call('key', 'fuel_sync_set_key', { p_key: key }, 'API key stored')) setKey('');
          }} loading={busy === 'key'} disabled={key.trim().length < 16}>Store key</Button>
          {c.has_key && (
            <Button variant="secondary" loading={busy === 'clear'}
              onClick={() => {
                if (window.confirm('Remove the API key? The sync stops until a new key is entered.')) {
                  void call('clear', 'fuel_sync_clear_key', undefined, 'API key removed');
                }
              }}>Remove key</Button>
          )}
        </div>
      </Panel>

      <Panel title="Connection">
        <div className="form-grid">
          <TextInput id="fs-url" label="Old platform address" value={form.base_url}
            onChange={(x) => setForm({ ...form, base_url: x })} help="https://os.iotechnam.com" />
          <TextInput id="fs-from" type="date" label="Take records from" value={form.sync_from}
            onChange={(x) => setForm({ ...form, sync_from: x })}
            help="The first run reads from here; the history import already covers what came before." />
          <TextInput id="fs-fills" label="Fills form" value={form.fills_form}
            onChange={(x) => setForm({ ...form, fills_form: x })} />
          <TextInput id="fs-receipts" label="Deliveries form" value={form.receipts_form}
            onChange={(x) => setForm({ ...form, receipts_form: x })} />
          <SelectInput id="fs-lookback" label="Re-read the last" value={String(form.lookback_days)}
            onChange={(x) => setForm({ ...form, lookback_days: Number(x ?? 7) })}
            options={[3, 7, 14, 30].map((d) => ({ value: String(d), label: `${d} days` }))} />
        </div>
        <CheckboxInput id="fs-enabled" label="Sync every 15 minutes" value={form.enabled}
          disabled={!c.has_key} onChange={(x) => setForm({ ...form, enabled: x })}
          help={c.has_key ? undefined : 'Store the API key first.'} />
        <div className="row" style={{ marginTop: 'var(--sp-2)' }}>
          <span className="spacer" />
          <Button onClick={() => void call('save', 'fuel_sync_save', { p: form }, 'Fuel sync settings saved')}
            loading={busy === 'save'}>Save</Button>
        </div>
      </Panel>

      <Panel title="Fuel points" flush>
        <p className="small muted" style={{ padding: 'var(--sp-3) var(--sp-3) 0', margin: 0 }}>
          The old platform's branch on each record, and the tank or fuel point it means here.
          {unlinked.length > 0 && ` ${unlinked.length} not linked: their records wait until they are.`}
        </p>
        <table className="table">
          <thead><tr><th>Old platform</th><th>Fuel point here</th></tr></thead>
          <tbody>
            {state.points.map((p) => (
              <tr key={p.branch}>
                <td>{p.branch}{!p.tank_id && <> <StatusChip tone="amber">Not linked</StatusChip></>}</td>
                <td style={{ maxWidth: 360 }}>
                  <select aria-label={`Fuel point for ${p.branch}`} value={p.tank_id ?? ''} disabled={busy === `pt-${p.branch}`}
                    onChange={(e) => void call(`pt-${p.branch}`, 'fuel_sync_set_point',
                      { p_branch: p.branch, p_tank: e.target.value || null }, `${p.branch} linked`)}>
                    <option value="">— not linked —</option>
                    {state.tanks.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>

      <Panel title="Runs" flush actions={<Button variant="secondary" onClick={() => { void load(); void syncNow(); }}>Refresh</Button>}>
        {state.runs.length === 0 ? (
          <p className="muted" style={{ padding: 'var(--sp-3)', margin: 0 }}>No runs yet.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Started</th><th>Status</th><th style={{ textAlign: 'right' }}>Read</th>
                  <th style={{ textAlign: 'right' }}>New</th><th style={{ textAlign: 'right' }}>Corrected</th>
                  <th style={{ textAlign: 'right' }}>Linked</th><th style={{ textAlign: 'right' }}>Unchanged</th>
                  <th style={{ textAlign: 'right' }}>Not taken</th><th>Note</th>
                </tr>
              </thead>
              <tbody>
                {state.runs.map((r) => (
                  <tr key={r.id}>
                    <td>{dateTime(r.started_at)}<div className="small muted">{r.trigger === 'manual' ? 'Sync now' : 'Schedule'}
                      {r.window_from ? ` · from ${fmtDate(r.window_from)}` : ''}</div></td>
                    <td><StatusChip tone={RUN_TONE[r.status]}>{r.status === 'ok' ? 'Done' : r.status === 'running' ? 'Running' : 'Failed'}</StatusChip></td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{num(r.fetched)}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{num(r.inserted)}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{num(r.updated)}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{num(r.linked + r.skipped)}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{num(r.unchanged)}</td>
                    <td className="tabular" style={{ textAlign: 'right' }}>{num(r.rejected)}</td>
                    <td className="small">{r.message ?? ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {state.rejects.length > 0 && (
        <Panel title={`Not taken (${state.totals.rejected})`} flush>
          <p className="small muted" style={{ padding: 'var(--sp-3) var(--sp-3) 0', margin: 0 }}>
            Tried again on every run: fix the cause (link the fuel point, or correct the record on the old platform)
            and it comes in by itself.
          </p>
          <table className="table">
            <thead><tr><th>Record</th><th>Fuel point</th><th>Vehicle / note</th><th style={{ textAlign: 'right' }}>Litres</th><th>Why</th></tr></thead>
            <tbody>
              {state.rejects.map((x) => (
                <tr key={`${x.form}-${x.record}`}>
                  <td>{x.record}<div className="small muted">{x.form === 'fills' ? 'Fill' : 'Delivery'}
                    {x.submitted_at ? ` · ${dateTime(x.submitted_at)}` : ''}</div></td>
                  <td>{x.branch ?? '—'}</td>
                  <td>{x.what ?? '—'}</td>
                  <td className="tabular" style={{ textAlign: 'right' }}>{x.litres ?? '—'}</td>
                  <td className="small">{x.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}
    </div>
  );
}
